import React from 'react';
import { MatIdleMatchRow, MatIdleView } from '../../utils/matDisplayApi';
import { RoundRobinStandingModel } from '../../utils/categoryDrawApi';

const isTbd = (name: string) => !name || name.trim().toUpperCase() === 'TBD';

const pairClass = (m: MatIdleMatchRow, side: 'home' | 'away') => {
  const homeTbd = isTbd(m.homeName);
  const awayTbd = isTbd(m.awayName);
  if (homeTbd !== awayTbd) {
    const advances = side === 'home' ? !homeTbd : !awayTbd;
    return advances ? 'is-winner' : 'is-tbd';
  }
  if (!m.finished || m.isHomeWinner == null) return undefined;
  const won = side === 'home' ? m.isHomeWinner : !m.isHomeWinner;
  return won ? 'is-winner' : 'is-loser';
};

const placeLabel = (place: number) => {
  if (place === 1) return '1-ое место';
  if (place === 2) return '2-ое место';
  return '3-е место';
};

const medalColors = {
  1: { metal: '#FFD700', dark: '#B8860B', ribbon: '#B45309' },
  2: { metal: '#E5E7EB', dark: '#9CA3AF', ribbon: '#64748B' },
  3: { metal: '#CD7F32', dark: '#8B5A2B', ribbon: '#9A3412' },
} as const;

const MedalIcon: React.FC<{ place: number }> = ({ place }) => {
  const colors = medalColors[place as 1 | 2 | 3] ?? medalColors[3];
  return (
    <svg className="mat-display__medal" viewBox="0 0 32 36" aria-hidden="true">
      <path d="M10 2 L16 14 L13 14 L7 2 Z" fill={colors.ribbon} />
      <path d="M22 2 L16 14 L19 14 L25 2 Z" fill={colors.ribbon} />
      <circle cx="16" cy="22" r="12" fill={colors.metal} />
      <circle cx="16" cy="22" r="9" fill="none" stroke={colors.dark} strokeWidth="1.6" />
      <text
        x="16"
        y="26.5"
        textAnchor="middle"
        fontSize="11"
        fontWeight="800"
        fill={colors.dark}
        fontFamily="Segoe UI, Arial, sans-serif"
      >
        {place}
      </text>
    </svg>
  );
};

const StandingsTable: React.FC<{ standings: RoundRobinStandingModel[] }> = ({ standings }) => {
  if (standings.length === 0) return null;
  return (
    <table className="mat-display__standings">
      <thead>
        <tr>
          <th>#</th>
          <th>Участник</th>
          <th>П</th>
          <th>О</th>
        </tr>
      </thead>
      <tbody>
        {standings.map(s => (
          <tr key={s.participantId}>
            <td>{s.place ?? '—'}</td>
            <td>{s.personName}</td>
            <td>{s.wins}</td>
            <td>{s.points}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MatchList: React.FC<{ matches: MatIdleMatchRow[]; showRound?: boolean }> = ({
  matches,
  showRound,
}) => (
  <ul className="mat-display__upcoming">
    {matches.map(m => (
      <li key={m.id}>
        {showRound && <span className="round">{m.roundLabel}</span>}
        <span className="pair">
          <span className={pairClass(m, 'home')}>{m.homeName}</span>
          {' — '}
          <span className={pairClass(m, 'away')}>{m.awayName}</span>
        </span>
      </li>
    ))}
  </ul>
);

const MatIdleContent: React.FC<{ view: MatIdleView | null }> = ({ view }) => {
  if (!view || view.idleMode === 'Wait') {
    return <div className="mat-display__idle-title">Ожидание поединка</div>;
  }

  const subtitle = [view.tournamentName, view.categoryLabel].filter(Boolean).join(' · ');

  if (view.idleMode === 'Upcoming') {
    if (view.upcoming.length === 0) {
      return (
        <>
          {subtitle && <div className="mat-display__idle-sub">{subtitle}</div>}
          <div className="mat-display__idle-title">Ожидание поединка</div>
        </>
      );
    }
    return (
      <>
        <div className="mat-display__idle-title">Ближайшие поединки</div>
        {subtitle && <div className="mat-display__idle-sub">{subtitle}</div>}
        <MatchList matches={view.upcoming} showRound />
      </>
    );
  }

  return (
    <div className="mat-display__idle-bracket">
      <div className="mat-display__idle-title">
        {view.format === 'RoundRobin' ? 'Таблица' : view.format === 'Mixed' ? 'Группы' : 'Сетка'}
      </div>
      {subtitle && <div className="mat-display__idle-sub">{subtitle}</div>}

      {view.standings.length > 0 && <StandingsTable standings={view.standings} />}

      {view.groups.length > 0 && (
        <div className="mat-display__idle-rounds">
          {view.groups.map(group => (
            <div key={group.groupIndex} className="mat-display__idle-group">
              <h4>Группа {group.groupIndex === 1 ? 'А' : group.groupIndex === 2 ? 'Б' : group.groupIndex}</h4>
              <StandingsTable standings={group.standings} />
              <MatchList matches={group.matches} showRound />
            </div>
          ))}
        </div>
      )}

      {view.rounds.length > 0 && (
        <div className="mat-display__idle-rounds">
          {view.rounds.map(round => (
            <div key={round.title} className="mat-display__idle-group">
              <h4>{round.title}</h4>
              <MatchList matches={round.matches} />
            </div>
          ))}
        </div>
      )}

      {(view.podium ?? []).length > 0 && (
        <div className="mat-display__podium">
          {view.podium.map((row, index) => (
            <div
              key={`${row.place}-${row.name}-${index}`}
              className={`mat-display__podium-row is-place-${row.place}`}
            >
              <MedalIcon place={row.place} />
              <span className="place">{placeLabel(row.place)}:</span>
              <span className="name">{row.name}</span>
            </div>
          ))}
        </div>
      )}

      {view.standings.length === 0 && view.groups.length === 0 && view.rounds.length === 0 && (
        <div className="mat-display__idle-title">Сетка ещё не утверждена</div>
      )}
    </div>
  );
};

export default MatIdleContent;
